<template>
  <div>
    <!-- Animated Support Banner -->
    <div class="support-banner mb-2">
      <div class="banner-content">
        <div class="banner-text">
          <div class="title-with-icon">
            <div class="banner-icon">
              <feather-icon
                icon="HeadphonesIcon"
                size="40"
              />
            </div>
            <h2 class="banner-title">
              Service Client
            </h2>
          </div>
          <p class="banner-subtitle">
            Notre équipe de support est à votre écoute. Nous sommes là pour garantir votre satisfaction.
          </p>
        </div>
      </div>
      <div class="banner-decoration">
        <div class="circle circle-1" />
        <div class="circle circle-2" />
        <div class="circle circle-3" />
        <div class="circle circle-4" />
        <div class="circle circle-5" />
      </div>
    </div>

    <!-- Support Team Contact Cards -->
    <div
      v-if="emergencyContacts.length > 0"
      class="support-team-section mb-3"
    >
      <h4 class="section-title mb-2">
        <feather-icon
          icon="UsersIcon"
          size="20"
          class="mr-50"
        />
        Notre Équipe de Support
      </h4>
      <b-row>
        <b-col
          v-for="contact in emergencyContacts"
          :key="contact.id"
          md="4"
          cols="12"
          class="mb-2"
        >
          <b-card class="support-member-card">
            <div class="member-avatar">
              <span class="avatar-initials">{{ getInitials(contact.name) }}</span>
            </div>
            <h5 class="member-name mt-1">
              {{ contact.name }}
            </h5>
            <p class="member-role">
              {{ contact.function }}
            </p>
            <div class="member-contact">
              <feather-icon
                icon="PhoneIcon"
                size="16"
                class="mr-50"
              />
              <a
                :href="'tel:' + contact.phone"
                class="phone-link"
              >
                {{ contact.phone }}
              </a>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Enhanced Tabs -->
    <b-card class="tabs-container">
      <b-tabs
        v-model="activeTab"
        nav-class="modern-tabs"
        content-class="mt-3"
        active-nav-item-class="support-tab-active"
        pills
        fill
      >
        <!-- Submit Ticket Tab -->
        <b-tab
          active
          title-link-class="support-tab-link"
        >
          <template #title>
            <feather-icon
              icon="PlusCircleIcon"
              size="16"
              class="mr-50"
            />
            <span>Soumettre un Ticket</span>
          </template>
          <ticket-types-selection @type-selected="selectTicketType" />
        </b-tab>

        <!-- My Tickets Tab -->
        <b-tab title-link-class="support-tab-link">
          <template #title>
            <feather-icon
              icon="ListIcon"
              size="16"
              class="mr-50"
            />
            <span>Mes Tickets</span>
          </template>
          <my-tickets-list />
        </b-tab>
      </b-tabs>
    </b-card>

    <!-- Enhanced New Ticket Modal -->
    <b-modal
      id="new-ticket-modal"
      v-model="showModal"
      size="lg"
      hide-footer
      modal-class="modern-modal"
      centered
    >
      <template #modal-header>
        <div class="modal-header-custom-vibrant">
          <div class="header-glow-effect" />
          <div class="modal-header-content">
            <!-- Icon Removed -->
            <div class="modal-title-wrapper">
              <!-- Badge Removed -->
              <h2 class="modal-title-vibrant">
                Créer un Ticket de Support
              </h2>
              <p class="modal-subtitle-vibrant">
                <feather-icon
                  icon="MessageCircleIcon"
                  size="14"
                  class="mr-50"
                />
                Décrivez votre problème ou demande en détail
              </p>
            </div>
          </div>
          <button
            type="button"
            class="close-button-vibrant"
            @click="showModal = false"
          >
            <feather-icon
              icon="XIcon"
              size="20"
            />
          </button>
        </div>
      </template>
      <ticket-form
        :ticket-type="selectedTicketType"
        @ticket-created="onTicketCreated"
        @cancel="showModal = false"
      />
    </b-modal>
  </div>
</template>

<script>
import {
  BTabs, BTab, BModal, BCard, BRow, BCol,
} from 'bootstrap-vue'
import axios from '@/libs/axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import TicketTypesSelection from './supplier-support/components/TicketTypesSelection.vue'
import MyTicketsList from './supplier-support/components/MyTicketsList.vue'
import TicketForm from './supplier-support/components/TicketForm.vue'

export default {
  name: 'SupplierSupportCenter',
  components: {
    BTabs,
    BTab,
    BModal,
    BCard,
    BRow,
    BCol,
    TicketTypesSelection,
    MyTicketsList,
    TicketForm,
  },
  data() {
    return {
      activeTab: 0,
      showModal: false,
      selectedTicketType: null,
      companyInfo: {
        company_name: 'Votre Société de Développement',
        support_phone: '+216 XX XXX XXX',
        support_phone_hours: 'Lun-Ven: 9H-18H, Sam: 9H-14H',
        support_email: 'support@deliveryapp.com',
        support_email_response_time: 'Réponse sous 24 heures',
        business_hours: 'Lun-Ven: 9H-18H, Sam: 9H-14H',
        emergency_contacts: [],
      },
      emergencyContacts: [],
    }
  },
  mounted() {
    this.fetchCompanyInfo()
  },
  methods: {
    async fetchCompanyInfo() {
      try {
        const response = await axios.get('/api/support/company-info/')
        this.companyInfo = response.data
        this.emergencyContacts = response.data.emergency_contacts?.filter(c => c.is_active) || []
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching company info:', error)
      }
    },
    getInitials(name) {
      if (!name) return ''
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },
    selectTicketType(type) {
      this.selectedTicketType = type
      this.showNewTicketModal()
    },
    showNewTicketModal() {
      this.showModal = true
    },
    // eslint-disable-next-line no-unused-vars
    onTicketCreated(ticket) {
      this.showModal = false
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Ticket Créé',
          text: 'Votre ticket a été créé avec succès.',
          variant: 'success',
          icon: 'CheckIcon',
        },
      })
      // Switch to My Tickets tab
      this.activeTab = 1
    },
  },
}
</script>

<style lang="scss">
// Hide footer on support center page
.footer {
  display: none !important;
}

/* Ensure active tab styling is applied globally (not affected by scope) */
.modern-tabs .nav-link.active,
.modern-tabs .nav-link.active:focus,
.modern-tabs .nav-link.active:hover {
  background: rgba(237, 25, 25, 0.1) !important;
  background-color: rgba(237, 25, 25, 0.1) !important;
  background-image: none !important;
  color: #1d1d1b !important;
  border-color: rgba(237, 25, 25, 0.2) !important;
  box-shadow: 0 4px 12px rgba(237, 25, 25, 0.15) !important;
}

.tabs-container .modern-tabs.nav.nav-pills .nav-link.active,
.tabs-container .modern-tabs.nav.nav-pills .nav-link.active:focus,
.tabs-container .modern-tabs.nav.nav-pills .nav-link.active:hover {
  background: rgba(237, 25, 25, 0.1) !important;
  background-color: rgba(237, 25, 25, 0.1) !important;
  background-image: none !important;
  color: #1d1d1b !important;
  border-color: rgba(237, 25, 25, 0.2) !important;
  box-shadow: 0 4px 12px rgba(237, 25, 25, 0.15) !important;
}

.nav.nav-pills.modern-tabs .nav-link.active,
.nav.nav-tabs.modern-tabs .nav-link.active {
  background: rgba(237, 25, 25, 0.1) !important;
  background-color: rgba(237, 25, 25, 0.1) !important;
  background-image: none !important;
  color: #1d1d1b !important;
  border-color: rgba(237, 25, 25, 0.2) !important;
  box-shadow: 0 4px 12px rgba(237, 25, 25, 0.15) !important;
}

/* Extra specificity to beat theme defaults */
.modern-tabs .nav-item .nav-link.active,
.nav.nav-pills.modern-tabs .nav-item .nav-link.active,
.nav.nav-tabs.modern-tabs .nav-item .nav-link.active {
  background: rgba(237, 25, 25, 0.1) !important;
  background-color: rgba(237, 25, 25, 0.1) !important;
  background-image: none !important;
  color: #1d1d1b !important;
  border-color: rgba(237, 25, 25, 0.2) !important;
  box-shadow: 0 4px 12px rgba(237, 25, 25, 0.15) !important;
}

.modern-tabs .nav-link.active svg {
  color: #1d1d1b !important;
}

.modern-tabs .nav-item.support-tab-active > .nav-link {
  background: rgba(237, 25, 25, 0.1) !important;
  background-color: rgba(237, 25, 25, 0.1) !important;
  background-image: none !important;
  color: #1d1d1b !important;
  border-color: rgba(237, 25, 25, 0.2) !important;
  box-shadow: 0 4px 12px rgba(237, 25, 25, 0.15) !important;
}

/* Stronger specificity to override theme defaults */
.tabs-container .modern-tabs.nav.nav-pills > .nav-item > .nav-link.active,
.tabs-container .modern-tabs.nav.nav-tabs > .nav-item > .nav-link.active,
.tabs-container .modern-tabs.nav.nav-pills > .nav-item > .nav-link.active:focus,
.tabs-container .modern-tabs.nav.nav-tabs > .nav-item > .nav-link.active:focus,
.tabs-container .modern-tabs.nav.nav-pills > .nav-item > .nav-link.active:hover,
.tabs-container .modern-tabs.nav.nav-tabs > .nav-item > .nav-link.active:hover {
  background: rgba(237, 25, 25, 0.1) !important;
  background-color: rgba(237, 25, 25, 0.1) !important;
  background-image: none !important;
  color: #1d1d1b !important;
  border-color: rgba(237, 25, 25, 0.2) !important;
  box-shadow: 0 4px 12px rgba(237, 25, 25, 0.15) !important;
}

.tabs-container .modern-tabs.nav.nav-pills > .nav-item > .nav-link.active svg,
.tabs-container .modern-tabs.nav.nav-tabs > .nav-item > .nav-link.active svg {
  color: #1d1d1b !important;
}
</style>

<style lang="scss" scoped>
.alert-heading {
  color: inherit !important;
}

// Modern Tabs Container
.tabs-container {
  border: none;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-radius: 1.25rem;
  overflow: hidden;
  background: white;

  ::v-deep .card-body {
    padding: 0;
  }
}

// Simple Modern Tabs Styling
::v-deep .modern-tabs {
  background: #ffebeb;
  border-bottom: 2px solid #ffcccc;
  padding: 0.75rem 1rem;
  margin-bottom: 0 !important;
  display: flex;
  gap: 0.5rem;

  .nav-item {
    margin-bottom: 0;
  }

  .nav-link {
    border: none;
    background: white;
    color: #6e6b7b;
    font-weight: 500;
    padding: 0.65rem 1.25rem;
    border-radius: 0.5rem;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    font-size: 0.9rem;
    border: 1px solid #e9ecef;

    svg {
      transition: all 0.3s ease;
    }

    &:hover {
      background: #f8f9fa;
      color: #ff9999;
      border-color: #ff9999;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(255, 153, 153, 0.15);

      svg {
        color: #ff9999;
      }
    }

    &.active,
    &.active:focus,
    &.active:hover {
      background: rgba(237, 25, 25, 0.1) !important;
      background-color: rgba(237, 25, 25, 0.1) !important;
      background-image: none !important;
      color: #1d1d1b !important;
      border-color: rgba(237, 25, 25, 0.2) !important;
      box-shadow: 0 4px 12px rgba(237, 25, 25, 0.15) !important;

      svg {
        color: #1d1d1b !important;
      }
    }
  }
}

/* Force active appearance via the link class itself */
::v-deep .support-tab-link.active {
  background: rgba(237, 25, 25, 0.1) !important;
  background-color: rgba(237, 25, 25, 0.1) !important;
  background-image: none !important;
  color: #1d1d1b !important;
  border-color: rgba(237, 25, 25, 0.2) !important;
  box-shadow: 0 4px 12px rgba(237, 25, 25, 0.15) !important;
}

::v-deep .support-tab-link.active svg {
  color: #1d1d1b !important;
}

/* Extra guard: force active tab styling inside this component */
::v-deep .modern-tabs .nav-link.active,
::v-deep .modern-tabs .nav-link.active:focus,
::v-deep .modern-tabs .nav-link.active:hover {
  background: rgba(237, 25, 25, 0.1) !important;
  background-color: rgba(237, 25, 25, 0.1) !important;
  background-image: none !important;
  color: #1d1d1b !important;
  border-color: rgba(237, 25, 25, 0.2) !important;
  box-shadow: 0 4px 12px rgba(237, 25, 25, 0.15) !important;
}

::v-deep .modern-tabs .nav-item.support-tab-active > .nav-link {
  background: rgba(237, 25, 25, 0.1) !important;
  background-color: rgba(237, 25, 25, 0.1) !important;
  background-image: none !important;
  color: #1d1d1b !important;
  border-color: rgba(237, 25, 25, 0.2) !important;
  box-shadow: 0 4px 12px rgba(237, 25, 25, 0.15) !important;
}

// Tab Content Styling
::v-deep .tab-content {
  padding: 2rem;
  background: white;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
}

/* Final override to force active tab appearance */
::v-deep .tabs-container .modern-tabs .nav-item > .nav-link.active,
::v-deep .tabs-container .modern-tabs .nav-item > .nav-link.active:focus,
::v-deep .tabs-container .modern-tabs .nav-item > .nav-link.active:hover {
  background: rgba(237, 25, 25, 0.1) !important;
  background-color: rgba(237, 25, 25, 0.1) !important;
  background-image: none !important;
  color: #1d1d1b !important;
  border-color: rgba(237, 25, 25, 0.2) !important;
  box-shadow: 0 4px 12px rgba(237, 25, 25, 0.15) !important;
}

::v-deep .tabs-container .modern-tabs .nav-item > .nav-link.active svg {
  color: #1d1d1b !important;
}

// Animated Support Banner
.support-banner {
  position: relative;
  background: linear-gradient(135deg, #000000 0%, #8B0000 100%);
  border-radius: 1rem;
  padding: 2.5rem 2rem;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(10, 38, 52, 0.4);
  animation: fadeInDown 0.8s ease-out;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
}

.banner-content {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: slideUp 0.8s ease-out 0.2s both;
}

.banner-text {
  color: white;
  text-align: center;
  max-width: 900px;
}

.title-with-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    gap: 0.75rem;
  }
}

.banner-icon {
  flex-shrink: 0;
  width: 70px;
  height: 70px;
  background: rgba(237, 25, 25, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ED1919;
  animation: floatBounce 3s ease-in-out infinite;
  box-shadow: 0 8px 32px rgba(237, 25, 25, 0.1);
  border: 2px solid rgba(237, 25, 25, 0.1);

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
}

.banner-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0;
  color: white;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
}

.banner-subtitle {
  font-size: 1.1rem;
  margin-bottom: 0;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.6;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
}

.banner-decoration {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  animation: propagate 4s ease-out infinite;
}

.circle-1 {
  width: 100px;
  height: 100px;
  top: 50%;
  left: 10%;
  animation-delay: 0s;
}

.circle-2 {
  width: 120px;
  height: 120px;
  top: 50%;
  left: 10%;
  animation-delay: 0.8s;
}

.circle-3 {
  width: 140px;
  height: 140px;
  top: 50%;
  left: 10%;
  animation-delay: 1.6s;
}

.circle-4 {
  width: 100px;
  height: 100px;
  top: 50%;
  right: 10%;
  animation-delay: 0.4s;
}

.circle-5 {
  width: 120px;
  height: 120px;
  top: 50%;
  right: 10%;
  animation-delay: 1.2s;
}

// Animations
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes propagate {
  0% {
    transform: translate(-50%, -50%) scale(0.3);
    opacity: 0.8;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    transform: translate(-50%, -50%) scale(2.5);
    opacity: 0;
  }
}

@keyframes floatBounce {
  0%, 100% {
    transform: translateY(0) scale(1);
    box-shadow: 0 8px 32px rgba(255, 255, 255, 0.3);
  }
  25% {
    transform: translateY(-10px) scale(1.05);
    box-shadow: 0 12px 40px rgba(255, 255, 255, 0.4);
  }
  50% {
    transform: translateY(0) scale(1);
    box-shadow: 0 8px 32px rgba(255, 255, 255, 0.3);
  }
  75% {
    transform: translateY(-5px) scale(1.02);
    box-shadow: 0 10px 36px rgba(255, 255, 255, 0.35);
  }
}

.support-header-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: none;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);

  h1 {
    font-size: 2rem;
    color: #5e5873;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
}

.confidence-badges {
  .badge {
    font-size: 0.85rem;
    padding: 0.5rem 0.75rem;
    font-weight: 500;
  }
}

.btn-create-ticket {
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(115, 103, 240, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
}

// Support Team Section
.support-team-section {
  .section-title {
    color: #5e5873;
    font-weight: 600;
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
  }
}

.support-member-card {
  text-align: center;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border-radius: 1rem;
  transition: all 0.3s ease;
  height: 100%;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }

  .member-avatar {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    box-shadow: 0 4px 15px rgba(30, 58, 95, 0.3);

    .avatar-initials {
      font-size: 1.75rem;
      font-weight: 700;
      color: white;
      letter-spacing: 2px;
    }
  }

  .member-name {
    font-size: 1.1rem;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 0.25rem;
  }

  .member-role {
    font-size: 0.9rem;
    color: #6c757d;
    margin-bottom: 1rem;
  }

  .member-contact {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #28a745;
    font-weight: 500;

    .phone-link {
      color: #28a745;
      text-decoration: none;
      transition: color 0.2s ease;

      &:hover {
        color: #218838;
        text-decoration: underline;
      }
    }
  }
}

// Enhanced Modal Styling with Vibrant Colors
::v-deep .modern-modal {
  .modal-content {
    border: none;
    border-radius: 1.75rem;
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
    overflow: hidden;
  }

  .modal-header {
    border: none;
    padding: 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
    position: relative;
    min-height: 90px;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, transparent 100%);
      pointer-events: none;
    }

    &::after {
      display: none;
    }
  }

  .modal-body {
    padding: 2.5rem;
    background: white;

    @media (max-width: 768px) {
      padding: 1.5rem;
    }
  }
}

@keyframes headerGlow {
  0%, 100% {
    transform: scale(1) translateY(0);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2) translateY(-10px);
    opacity: 0.8;
  }
}

.modal-header-custom-vibrant {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem 1.25rem 2rem;
  width: 100%;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;
  }

  .modal-header-content {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    flex: 1;
    position: relative;
    z-index: 2;

    @media (max-width: 768px) {
      gap: 1rem;
    }
  }
}

.header-glow-effect {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 60%);
  pointer-events: none;
  z-index: 1;
}

.modal-icon-vibrant {
  width: 60px;
  height: 60px;
  background: rgba(237, 25, 25, 0.1);
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ED1919;
  flex-shrink: 0;
  box-shadow: none;
  border: none;
  position: relative;

  @media (max-width: 768px) {
    width: 52px;
    height: 52px;
  }

  svg {
    stroke-width: 2.5px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
    position: relative;
    z-index: 2;
  }

  .icon-pulse-ring {
    display: none;
  }
}

@keyframes iconBounceGlow {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-3px) scale(1.02);
  }
}

@keyframes pulseRing {
  0% {
    transform: scale(0.9);
    opacity: 1;
  }
  100% {
    transform: scale(1.3);
    opacity: 0;
  }
}

.title-badge {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #FFD93D 0%, #FFC107 100%);
  color: #1a1a2e;
  padding: 0.3rem 0.85rem;
  border-radius: 1.5rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 3px 8px rgba(255, 193, 7, 0.3);

  svg {
    animation: none;
  }
}

@keyframes badgePulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes zapRotate {
  0%, 100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(-10deg);
  }
  75% {
    transform: rotate(10deg);
  }
}

.modal-title-wrapper {
  flex: 1;
}

.modal-title-vibrant {
  color: #2c3e50;
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 0.4rem;
  letter-spacing: -0.3px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
}

.modal-subtitle-vibrant {
  color: #5e5873;
  font-size: 0.9rem;
  margin-bottom: 0;
  font-weight: 400;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }

  svg {
    flex-shrink: 0;
    color: #FF6B6B;
  }
}

.close-button-vibrant {
  width: 40px;
  height: 40px;
  width: 40px;
  height: 40px;
  background: rgba(237, 25, 25, 0.1);
  backdrop-filter: blur(10px);
  border: none;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ED1919;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  flex-shrink: 0;
  box-shadow: none;

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }

  &:hover {
    background: rgba(237, 25, 25, 0.15);
    transform: rotate(90deg) scale(1.05);
    box-shadow: none;
  }

  &:active {
    transform: rotate(90deg) scale(0.95);
  }

  svg {
    stroke-width: 2.5px;
  }
}
</style>
